import React, { useState, useEffect, useCallback } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Landmark, ShieldCheck, Download, Loader2, CheckCircle2, FileText, Printer, CreditCard, XCircle } from "lucide-react";
import axios from "axios";
import { FIRM } from "@/lib/firm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

// Client-facing page: view the clean-energy (solar, IRC §6418) tax-credit
// transfer offer, pay the program fee via Stripe, set up the $9.99/mo card on
// file, then download/print the §6418 Transfer Election Statement and the full
// tax-resolution package (official IRS request forms) for filing.
export default function TaxCreditClient() {
  const { appId } = useParams();
  const search = new URLSearchParams(useLocation().search);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");
  const [buying, setBuying] = useState(false);
  const [paid, setPaid] = useState(false);
  const [paidSession, setPaidSession] = useState(search.get("session") || "");
  const [efax, setEfax] = useState(null);
  const [checking, setChecking] = useState(false);
  const [billing, setBilling] = useState(null);
  const [settingCard, setSettingCard] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [cancelling, setCancelling] = useState(false);

  const btoken = localStorage.getItem("th_borrower_token");
  const bcfg = btoken ? { headers: { Authorization: `Bearer ${btoken}` } } : {};

  const loadBilling = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/tax-program/billing/receipts`, bcfg);
      setReceipts(r.data.receipts || []);
      if (r.data.monthly_billing) setBilling(r.data.monthly_billing);
    } catch { /* not signed in / no enrollment */ }
    // eslint-disable-next-line
  }, []);

  const cancelBilling = async () => {
    if (!window.confirm("Cancel the $9.99/month service fee? Your card will be removed.")) return;
    setCancelling(true);
    try {
      await axios.post(`${API}/tax-program/billing/cancel`, {}, bcfg);
      setBilling({ enrolled: false });
    } catch (e) { setErr(e.response?.data?.detail || "Could not cancel billing."); }
    finally { setCancelling(false); }
  };

  useEffect(() => {
    (async () => {
      try { const r = await axios.get(`${API}/tax-credit/quote/${appId}`); setQuote(r.data); }
      catch (e) { setErr(e.response?.data?.detail || "Unable to load the tax-credit offer."); }
    })();
  }, [appId]);

  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt > 10) { setChecking(false); return; }
    setChecking(true);
    try {
      const r = await axios.get(`${API}/tax-credit/status/${sessionId}`);
      if (r.data.paid) { setPaid(true); setPaidSession(sessionId); setEfax(r.data.efax || null); setChecking(false); return; }
      if (r.data.status === "expired") { setChecking(false); return; }
    } catch { /* retry */ }
    setTimeout(() => pollStatus(sessionId, attempt + 1), 1800);
  }, []);

  const confirmCardSetup = useCallback(async (sessionId) => {
    try {
      const r = await axios.get(`${API}/tax-program/billing/status/${sessionId}`, bcfg);
      if (r.data.enrolled) setBilling(r.data.monthly_billing || { enrolled: true });
    } catch { /* noop */ }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const sess = search.get("session");
    if (search.get("paid") === "1" && sess) pollStatus(sess);
    const bset = search.get("billing_setup");
    if (bset) confirmCardSetup(bset);
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (paid) loadBilling(); }, [paid, loadBilling]);

  const purchase = async () => {
    setBuying(true); setErr("");
    try {
      const r = await axios.post(`${API}/tax-credit/checkout`, { app_id: appId, origin_url: window.location.origin });
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { setErr(e.response?.data?.detail || "Could not start checkout."); setBuying(false); }
  };

  const setupCard = async () => {
    setSettingCard(true); setErr("");
    try {
      const r = await axios.post(`${API}/tax-program/billing/setup`, { origin_url: window.location.origin }, bcfg);
      if (r.data.url) window.location.href = r.data.url;
    } catch (e) { setErr(e.response?.data?.detail || "Could not start card setup. Please sign in to your account first."); setSettingCard(false); }
  };

  const download = async (form) => {
    try {
      const url = `${API}/tax-credit/${appId}/document.pdf?session_id=${encodeURIComponent(paidSession)}${form ? `&form=${form}` : ""}`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blob; a.download = form === "election" ? "sec6418_election_statement.pdf" : "tax_resolution_package.pdf";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(blob);
    } catch (e) { setErr(e.response?.data?.detail || "Download failed."); }
  };

  const printDoc = async (form) => {
    try {
      const url = `${API}/tax-credit/${appId}/document.pdf?session_id=${encodeURIComponent(paidSession)}${form ? `&form=${form}` : ""}`;
      const r = await axios.get(url, { responseType: "blob" });
      const blob = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open(blob, "_blank");
      if (w) w.onload = () => { try { w.focus(); w.print(); } catch { /* noop */ } };
    } catch (e) { setErr(e.response?.data?.detail || "Could not open the document to print."); }
  };

  const CardPanel = () => (
    <div data-testid="tc-client-billing" style={{ background: billing?.enrolled ? "#07251a" : "#0A241A", border: `1px solid #14503B`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
      {billing?.enrolled ? (
        <>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6FD3AE", fontSize: 12.5 }}>
            <CheckCircle2 size={16} /> Monthly service fee active — <strong>$9.99/mo</strong> on the card ending •••• {billing.card_last4 || "••••"}, charged on the 1st.
          </div>
          <button onClick={cancelBilling} disabled={cancelling} data-testid="tc-client-cancel-billing"
            style={{ marginTop: 10, background: "transparent", color: "#FF9A9A", border: "1px solid #7A3A3A", borderRadius: 8, padding: "8px 12px", fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 }}>
            {cancelling ? <><Loader2 className="animate-spin" size={13} /> Cancelling…</> : <><XCircle size={13} /> Cancel monthly billing</>}
          </button>
        </>
      ) : billing && billing.enrolled === false && billing.cancelled_at ? (
        <div style={{ fontSize: 12.5, color: "#E4C97A" }}>Monthly billing cancelled. You can re-enroll anytime below.</div>
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: "#C8DAD2", marginBottom: 8 }}>Add a card for the recurring <strong>$9.99/month</strong> Seutrade service fee (charged on the 1st of each month until cancelled).</div>
          <button onClick={setupCard} disabled={settingCard} data-testid="tc-client-setup-card"
            style={{ width: "100%", background: "transparent", color: "#6FD3AE", border: "1px solid #14503B", borderRadius: 10, padding: "12px", fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            {settingCard ? <><Loader2 className="animate-spin" size={15} /> Redirecting…</> : <><CreditCard size={15} /> Set up $9.99/mo card on file</>}
          </button>
        </>
      )}
      {receipts.length > 0 && (
        <div data-testid="tc-client-receipts" style={{ marginTop: 12, borderTop: "1px solid #14503B", paddingTop: 10 }}>
          <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: ".1em", color: "#7FA496", marginBottom: 6 }}>Receipts</div>
          {receipts.slice(0, 6).map((r) => (
            <div key={r.id} style={{ display: "flex", justifyContent: "space-between", fontSize: 11.5, color: "#C8DAD2", padding: "3px 0" }}>
              <span>{(r.created_at || "").slice(0, 10)} · {r.kind === "monthly_fee" ? "Monthly fee" : "Program fee"}{r.period ? ` (${r.period})` : ""}</span>
              <span style={{ color: "#6FD3AE", fontWeight: 700 }}>{money(r.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif" }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "40px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <Landmark size={22} color="#6FD3AE" />
          <span style={{ fontSize: 12, letterSpacing: ".18em", textTransform: "uppercase", color: "#6FD3AE", fontWeight: 700 }}>{FIRM.name}</span>
        </div>
        <h1 style={{ fontFamily: "Georgia,serif", fontSize: 30, margin: "0 0 6px" }}>Clean Energy (Solar) Tax Credit Transfer</h1>
        <p style={{ color: "#9DB8AD", fontSize: 14, marginBottom: 26 }}>
          Under IRC §6418, {FIRM.name} transfers you an eligible §48 solar Investment Tax Credit for cash. You claim it on your federal income
          tax return (Form 3800 + the §6418 Transfer Election Statement) to reduce your income-tax liability. We also prepare your official IRS
          request forms (2848, 8821, 12277, 911, 9465, 433-F, 843, 656, 1040-V) to address any existing lien, levy or balance.
        </p>

        <div data-testid="tc-client-howitworks" style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 14, padding: "18px 20px", marginBottom: 22 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: 12 }}>How your program works</div>
          {[
            ["Enroll & authorize", "You enroll and authorize the one-time program fee plus the $9.99/month service fee. A secure client file opens for you."],
            ["We file your IRS forms", "Your selected official IRS forms are auto-filled and eFaxed to the IRS to address any lien, levy or balance — no paperwork for you."],
            ["Receive your solar credit", "Under IRC §6418 we transfer you an eligible §48 solar credit and issue your NON-NEGOTIABLE Transfer Certificate + §6418 Election Statement."],
            ["Claim it on your return", "Attach the Election Statement to your federal return and claim the credit on Form 3800. It reduces your federal income-tax liability."],
            ["Verify & keep records", "Scan the QR on your certificate anytime to verify it. Your monthly fee keeps representation active; cancel anytime from this page."],
          ].map(([t, d], i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: i === 4 ? 0 : 12 }}>
              <div style={{ flex: "0 0 auto", width: 24, height: 24, borderRadius: "50%", background: "#6FD3AE", color: "#06160F", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>{i + 1}</div>
              <div>
                <div style={{ fontWeight: 700, color: "#E7F3EE", fontSize: 13.5 }}>{t}</div>
                <div style={{ color: "#9DB8AD", fontSize: 12.5, lineHeight: 1.5 }}>{d}</div>
              </div>
            </div>
          ))}
        </div>

        {err && <div data-testid="tc-client-error" style={{ background: "#2a1212", border: "1px solid #7A3A3A", color: "#FF9A9A", padding: "10px 14px", borderRadius: 8, marginBottom: 18, fontSize: 13 }}>{err}</div>}

        {!quote && !err && <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9DB8AD" }}><Loader2 className="animate-spin" size={16} /> Loading your offer…</div>}

        {quote && (
          <div data-testid="tc-client-card" style={{ background: "#0A241A", border: "1px solid #14503B", borderRadius: 14, padding: 24 }}>
            <Row k="Taxpayer" v={quote.borrower_name || "—"} />
            <Row k="Eligible credit transferred" v={money(quote.tax_debt)} big />
            <Row k="Program fee (one-time)" v={money(quote.fee)} highlight />
            <div style={{ fontSize: 11.5, color: "#7FA496", marginTop: 4 }}>{quote.breakdown} Plus a recurring $9.99/month service fee.</div>

            {!paid ? (
              <>
                <button onClick={purchase} disabled={buying || checking} data-testid="tc-client-buy"
                  style={{ marginTop: 22, width: "100%", background: "#6FD3AE", color: "#06160F", border: "none", borderRadius: 10, padding: "14px", fontSize: 14, fontWeight: 800, letterSpacing: ".04em", textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  {buying ? <><Loader2 className="animate-spin" size={16} /> Redirecting…</> : checking ? <><Loader2 className="animate-spin" size={16} /> Confirming payment…</> : <><ShieldCheck size={16} /> Pay Program Fee — {money(quote.fee)}</>}
                </button>
                <p style={{ fontSize: 11, color: "#7FA496", marginTop: 10, textAlign: "center" }}>Secure checkout via Stripe. You'll return here to set up monthly billing and download your documents.</p>
              </>
            ) : (
              <div data-testid="tc-client-paid" style={{ marginTop: 22 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#6FD3AE", fontWeight: 800, marginBottom: 14 }}><CheckCircle2 size={18} /> Payment received — your documents are ready.</div>

                <CardPanel />

                <div data-testid="tc-client-efax" style={{ background: efax && efax.ok ? "#07251a" : "#241a07", border: `1px solid ${efax && efax.ok ? "#14503B" : "#7a5a12"}`, borderRadius: 10, padding: "12px 14px", marginBottom: 14, fontSize: 12.5 }}>
                  {efax && efax.ok
                    ? <span style={{ color: "#6FD3AE" }}>✓ Your IRS request forms have been <strong>prepared and submitted to the IRS</strong> for consideration.</span>
                    : <span style={{ color: "#E4C97A" }}>Your IRS request forms are prepared and being submitted to the IRS. You can also print and mail them using the instructions below.</span>}
                </div>

                <button onClick={() => download("election")} data-testid="tc-client-dl-election"
                  style={{ width: "100%", background: "#6FD3AE", color: "#06160F", border: "none", borderRadius: 10, padding: "13px", fontSize: 13.5, fontWeight: 800, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
                  <Download size={16} /> Download §6418 Transfer Election Statement
                </button>
                <button onClick={() => download("certificate")} data-testid="tc-client-dl-certificate"
                  style={{ width: "100%", background: "transparent", color: "#E4C97A", border: "1px solid #7a5a12", borderRadius: 10, padding: "12px", fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 10 }}>
                  <FileText size={15} /> Download Transfer Certificate (check-size)
                </button>
                <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
                  <button onClick={() => download("")} data-testid="tc-client-dl-package"
                    style={{ flex: 1, background: "transparent", color: "#6FD3AE", border: "1px solid #14503B", borderRadius: 10, padding: "13px", fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <FileText size={15} /> Download Full Package
                  </button>
                  <button onClick={() => printDoc("")} data-testid="tc-client-print-package"
                    style={{ flex: 1, background: "transparent", color: "#E4C97A", border: "1px solid #7a5a12", borderRadius: 10, padding: "13px", fontSize: 12.5, fontWeight: 700, textTransform: "uppercase", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Printer size={15} /> Print Package
                  </button>
                </div>

                <div data-testid="tc-client-mail-instructions" style={{ background: "#0A241A", border: "1px dashed #14503B", borderRadius: 10, padding: "14px 16px", marginTop: 6 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: 8 }}>How to file</div>
                  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: "#C8DAD2", lineHeight: 1.7 }}>
                    <li>Attach the §6418 Transfer Election Statement to your federal income tax return with <strong>Form 3800</strong> for the eligible year.</li>
                    <li>Sign the official IRS request forms (2848, 12277, 911, etc.) where indicated.</li>
                    <li>Mail or fax the signed request forms to the IRS office shown on each form.</li>
                    <li>Keep a copy for your records; your request forms are also submitted to the IRS on your behalf.</li>
                  </ol>
                  <div style={{ fontSize: 11, color: "#7FA496", marginTop: 8 }}>IRS Centralized Lien Operation — P.O. Box 145595, Cincinnati, OH 45250-5595 (verify the correct address for your notice before mailing).</div>
                </div>

                <p style={{ fontSize: 11, color: "#7FA496", marginTop: 12 }}>The full package includes your SEUTRADE Master Solar Tax Credit Transfer Agreement with the §6418 Transfer Election Statement, your Retainer &amp; Representation Agreement, Limited Power of Attorney, and the official IRS request forms you selected (2848, 8821, 12277, 911, 9465, 433-F, 843, 656, 1040-V).</p>
              </div>
            )}
          </div>
        )}
        <p style={{ fontSize: 10.5, color: "#5f7a6d", marginTop: 20, lineHeight: 1.5 }}>
          The transferred credit reduces your federal income-tax liability on a filed return and is subject to the general business credit,
          passive activity and at-risk limitations and possible recapture under §50. The documents are not issued, insured, or guaranteed by
          the United States, the Treasury, or the IRS, and relief depends on IRS review and your own circumstances.
        </p>
      </div>
    </div>
  );
}

const Row = ({ k, v, big, highlight }) => (
  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "9px 0", borderBottom: "1px solid #123c2c" }}>
    <span style={{ fontSize: 12.5, color: "#9DB8AD", textTransform: "uppercase", letterSpacing: ".06em" }}>{k}</span>
    <span style={{ fontSize: big ? 22 : highlight ? 20 : 15, fontWeight: highlight ? 800 : 700, color: highlight ? "#6FD3AE" : "#E7F3EE", fontFamily: big || highlight ? "Georgia,serif" : "inherit" }}>{v}</span>
  </div>
);
